import React, { useState, useEffect } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';
import LogoUpload from '../LogoUpload';
import { uploadAndSetLogo } from '../../utils/logoService';

const BusinessSetup = ({ data, onSave, onBack, isLoading }) => {
  const { currentOrganization, updateOrganization } = useOrganization();
  const [formData, setFormData] = useState({
    businessName: '',
    businessType: '',
    customBusinessType: '',
    timezone: 'America/New_York', // Default to Eastern
    businessPhone: '',
    googleReviewUrl: '',
    yelpUrl: '',
    facebookUrl: '',
    primaryReviewPlatform: 'google', // Default to Google
    ...data
  });

  const [errors, setErrors] = useState({});
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Pre-populate with organization data if available
  useEffect(() => {
    if (currentOrganization) {
      setFormData(prev => ({
        ...prev,
        businessName: prev.businessName, // Don't auto-populate business name
        businessType: currentOrganization.business_type || prev.businessType,
        timezone: currentOrganization.timezone || prev.timezone,
        businessPhone: currentOrganization.business_phone || prev.businessPhone,
        googleReviewUrl: currentOrganization.google_review_url || prev.googleReviewUrl,
        yelpUrl: currentOrganization.yelp_url || prev.yelpUrl,
        facebookUrl: currentOrganization.facebook_url || prev.facebookUrl,
        primaryReviewPlatform: currentOrganization.primary_review_platform || prev.primaryReviewPlatform
      }));
    }
  }, [currentOrganization]);

  const businessTypes = [
    { value: 'hvac', label: 'HVAC & Heating' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'cleaning', label: 'Cleaning Services' },
    { value: 'landscaping', label: 'Landscaping' },
    { value: 'roofing', label: 'Roofing' },
    { value: 'painting', label: 'Painting' },
    { value: 'handyman', label: 'Handyman Services' },
    { value: 'pest-control', label: 'Pest Control' },
    { value: 'appliance-repair', label: 'Appliance Repair' },
    { value: 'automotive', label: 'Automotive Services' },
    { value: 'beauty-wellness', label: 'Beauty & Wellness' },
    { value: 'home-improvement', label: 'Home Improvement' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'legal', label: 'Legal Services' },
    { value: 'real-estate', label: 'Real Estate' },
    { value: 'restaurant', label: 'Restaurant & Food' },
    { value: 'retail', label: 'Retail' },
    { value: 'professional-services', label: 'Professional Services' },
    { value: 'other', label: 'Other' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
    { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
    { value: 'America/Toronto', label: 'Eastern Time - Toronto' },
    { value: 'America/Vancouver', label: 'Pacific Time - Vancouver' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Central European Time' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time' },
    { value: 'Australia/Sydney', label: 'Australian Eastern Time' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Auto-set primary platform when URLs change
      if (['googleReviewUrl', 'yelpUrl', 'facebookUrl'].includes(field)) {
        const filledPlatforms = [];
        if (newData.googleReviewUrl && newData.googleReviewUrl.trim()) filledPlatforms.push('google');
        if (newData.yelpUrl && newData.yelpUrl.trim()) filledPlatforms.push('yelp');
        if (newData.facebookUrl && newData.facebookUrl.trim()) filledPlatforms.push('facebook');
        
        // If only one platform has a URL, make it the primary
        if (filledPlatforms.length === 1) {
          newData.primaryReviewPlatform = filledPlatforms[0];
        }
        // If the current primary platform no longer has a URL, switch to the first available
        else if (filledPlatforms.length > 0 && !filledPlatforms.includes(newData.primaryReviewPlatform)) {
          newData.primaryReviewPlatform = filledPlatforms[0];
        }
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
    
    // Clear review platforms error when any URL is added
    if (['googleReviewUrl', 'yelpUrl', 'facebookUrl'].includes(field) && value.trim() && errors.reviewPlatforms) {
      setErrors(prev => ({
        ...prev,
        reviewPlatforms: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    
    if (!formData.businessType) {
      newErrors.businessType = 'Please select a business type';
    }
    
    if (formData.businessType === 'other' && !formData.customBusinessType.trim()) {
      newErrors.customBusinessType = 'Please specify your business type';
    }

    // At least one review platform URL is required
    const hasGoogleUrl = formData.googleReviewUrl && formData.googleReviewUrl.trim();
    const hasYelpUrl = formData.yelpUrl && formData.yelpUrl.trim();
    const hasFacebookUrl = formData.facebookUrl && formData.facebookUrl.trim();
    
    if (!hasGoogleUrl && !hasYelpUrl && !hasFacebookUrl) {
      newErrors.reviewPlatforms = 'Please provide at least one review platform URL. This is where review requests will direct customers.';
    }

    // Validate URLs if provided
    const urlFields = ['googleReviewUrl', 'yelpUrl', 'facebookUrl'];
    urlFields.forEach(field => {
      if (formData[field] && formData[field].trim()) {
        const url = formData[field].trim();
        try {
          new URL(url.startsWith('http') ? url : `https://${url}`);
        } catch {
          newErrors[field] = 'Please enter a valid URL';
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      setLogoUploading(true);
      
      // Determine the final business type
      const finalBusinessType = formData.businessType === 'other' 
        ? formData.customBusinessType 
        : formData.businessType;

      // Normalize URLs to include https:// if not present
      const normalizeUrl = (url) => {
        if (!url || !url.trim()) return null;
        const trimmed = url.trim();
        return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
      };
      
      // Update organization with business details
      const updateResult = await updateOrganization({
        name: formData.businessName,
        business_name: formData.businessName,
        business_type: finalBusinessType,
        timezone: formData.timezone,
        business_phone: formData.businessPhone,
        google_review_url: normalizeUrl(formData.googleReviewUrl),
        yelp_url: normalizeUrl(formData.yelpUrl),
        facebook_url: normalizeUrl(formData.facebookUrl),
        primary_review_platform: formData.primaryReviewPlatform
      });
      
      if (!updateResult.success) {
        console.error('Failed to update organization:', updateResult.error);
        throw new Error(updateResult.error || 'Failed to save business information');
      }
      
      // Upload logo if one was selected
      if (selectedLogoFile && currentOrganization) {
        const logoResult = await uploadAndSetLogo(currentOrganization.id, selectedLogoFile);
        if (!logoResult.success) {
          console.warn('Logo upload failed:', logoResult.error);
          // Continue with onboarding even if logo upload fails
        }
      }
      
      onSave({
        ...formData,
        logoUploaded: !!selectedLogoFile
      });
    } catch (error) {
      console.error('Error saving business setup:', error);
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <div className="w-12 h-12 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Building" size={24} className="text-primary" />
        </div>
        <h2 className="text-xl font-semibold text-text-primary mb-2">
          Set up your business profile
        </h2>
        <p className="text-text-secondary">
          Help us connect your review platforms and customize ServiceReviewAI for your business.
        </p>
      </div>

      {/* Business Name */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Business Name <span className="text-danger">*</span>
        </label>
        <Input
          value={formData.businessName}
          onChange={(e) => handleInputChange('businessName', e.target.value)}
          placeholder="e.g., Smith's HVAC Services"
          error={errors.businessName}
          className="w-full"
        />
      </div>

      {/* Business Type */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Business Type <span className="text-danger">*</span>
        </label>
        <select
          value={formData.businessType}
          onChange={(e) => handleInputChange('businessType', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${
            errors.businessType ? 'border-danger' : 'border-border'
          }`}
        >
          <option value="">Select your business type</option>
          {businessTypes.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {errors.businessType && (
          <p className="text-danger text-sm mt-1">{errors.businessType}</p>
        )}
      </div>

      {/* Custom Business Type (when Other is selected) */}
      {formData.businessType === 'other' && (
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Specify Your Business Type <span className="text-danger">*</span>
          </label>
          <Input
            value={formData.customBusinessType}
            onChange={(e) => handleInputChange('customBusinessType', e.target.value)}
            placeholder="e.g., Pool Services, Security Systems, Home Inspection"
            error={errors.customBusinessType}
            className="w-full"
          />
        </div>
      )}

      {/* Timezone */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Timezone <span className="text-danger">*</span>
        </label>
        <select
          value={formData.timezone}
          onChange={(e) => handleInputChange('timezone', e.target.value)}
          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
        >
          {timezoneOptions.map(tz => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-text-secondary mt-1">
          Select your business timezone for scheduling and automation
        </p>
      </div>

      {/* Business Phone */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Business Phone
        </label>
        <Input
          value={formData.businessPhone}
          onChange={(e) => handleInputChange('businessPhone', e.target.value)}
          placeholder="(555) 123-4567"
          className="w-full"
        />
      </div>

      {/* Business Logo (Optional) */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Business Logo (Optional)
        </label>
        <LogoUpload
          onFileSelect={setSelectedLogoFile}
          onFileRemove={() => setSelectedLogoFile(null)}
          initialLogoUrl={currentOrganization?.logo_url}
          disabled={logoUploading}
          showConstraints={false}
          className="mb-2"
        />
        <p className="text-sm text-text-secondary">
          Your logo will appear in review requests and customer communications. You can add or change this later in settings.
        </p>
      </div>

      {/* External Links Section */}
      <div className="border-t border-border pt-6">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-text-primary mb-2">
            Review Platform Links <span className="text-danger">*</span>
          </h3>
          <p className="text-sm text-text-secondary">
            Connect your review platforms to track and manage all your reviews in one place. At least one platform is required for sending review requests.
          </p>
          {errors.reviewPlatforms && (
            <p className="text-danger text-sm mt-2 flex items-center space-x-1">
              <Icon name="AlertCircle" size={14} />
              <span>{errors.reviewPlatforms}</span>
            </p>
          )}
        </div>


        {/* Google My Business / Review URL */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-primary mb-2">
            Google Review Link
          </label>
          <Input
            value={formData.googleReviewUrl}
            onChange={(e) => handleInputChange('googleReviewUrl', e.target.value)}
            placeholder="https://g.page/r/your-business-id/review"
            error={errors.googleReviewUrl}
            className="w-full"
          />
          <p className="text-xs text-text-secondary mt-1">
            Direct link for customers to leave Google reviews (from Google My Business)
          </p>
        </div>

        {/* Yelp URL */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-primary mb-2">
            Yelp Business Page
          </label>
          <Input
            value={formData.yelpUrl}
            onChange={(e) => handleInputChange('yelpUrl', e.target.value)}
            placeholder="https://www.yelp.com/biz/your-business-name"
            error={errors.yelpUrl}
            className="w-full"
          />
          <p className="text-xs text-text-secondary mt-1">
            Link to your Yelp business page for customer reviews
          </p>
        </div>

        {/* Facebook URL */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-primary mb-2">
            Facebook Business Page
          </label>
          <Input
            value={formData.facebookUrl}
            onChange={(e) => handleInputChange('facebookUrl', e.target.value)}
            placeholder="https://www.facebook.com/yourbusiness"
            error={errors.facebookUrl}
            className="w-full"
          />
          <p className="text-xs text-text-secondary mt-1">
            Link to your Facebook business page for social reviews and engagement
          </p>
        </div>

        {/* Primary Platform Selection - Only show if multiple URLs are provided */}
        {(() => {
          const filledPlatforms = [];
          if (formData.googleReviewUrl && formData.googleReviewUrl.trim()) filledPlatforms.push({ id: 'google', name: 'Google Reviews' });
          if (formData.yelpUrl && formData.yelpUrl.trim()) filledPlatforms.push({ id: 'yelp', name: 'Yelp' });
          if (formData.facebookUrl && formData.facebookUrl.trim()) filledPlatforms.push({ id: 'facebook', name: 'Facebook' });
          
          if (filledPlatforms.length > 1) {
            return (
              <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <h4 className="text-sm font-medium text-primary-900 mb-3">
                  <Icon name="Star" size={16} className="inline mr-2" />
                  Primary Review Platform
                </h4>
                <p className="text-sm text-primary-700 mb-3">
                  Which platform should be your primary destination for review requests? You can change this later in settings.
                </p>
                <div className="space-y-2">
                  {filledPlatforms.map(platform => (
                    <label key={platform.id} className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="primaryReviewPlatform"
                        value={platform.id}
                        checked={formData.primaryReviewPlatform === platform.id}
                        onChange={(e) => handleInputChange('primaryReviewPlatform', e.target.value)}
                        className="w-4 h-4 text-primary border-primary-300 focus:ring-primary focus:ring-2"
                      />
                      <span className="text-sm font-medium text-primary-900">{platform.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          }
          return null;
        })()}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-900 mb-1">
                Why connect your review platforms?
              </h4>
              <p className="text-sm text-blue-700">
                ServiceReviewAI can help direct customers to the right review platform, track your online reputation, and send targeted review requests to improve your ratings across all platforms.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-6">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={isLoading}
        >
          <Icon name="ArrowLeft" size={16} className="mr-2" />
          Go Back
        </Button>
        <Button
          onClick={handleSave}
          disabled={isLoading || logoUploading}
          className="px-8"
        >
          {logoUploading ? 'Uploading Logo...' : isLoading ? 'Saving...' : 'Continue Setup'}
          <Icon name="ArrowRight" size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default BusinessSetup; 